'use client';

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/common/dialog/Dialog';
import { Button } from '@/components/common/Button';
import TaggedSelect from '@/components/common/TaggedSelect';

interface ArticleMetaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  status: string;
  onStatusChange: (status: string) => void;
  category: string;
  onCategoryChange: (category: string) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: { value: string; label: string }[];
  availableCategories: { value: string; label: string }[];
  onCreateTag: (tagName: string) => void;
  citations: string[];
  onCitationsChange: (citations: string[]) => void;
  mentions: string[];
  onMentionsChange: (mentions: string[]) => void;
}

const ArticleMetaDialog: React.FC<ArticleMetaDialogProps> = ({ isOpen, onClose, onSave, status, onStatusChange, category, onCategoryChange, tags, onTagsChange, availableTags, availableCategories, onCreateTag, citations, onCitationsChange, mentions, onMentionsChange }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Article Metadata</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <TaggedSelect value={status} disabled onChange={() => { }} options={[{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }, { value: 'archived', label: 'Archived' }]} placeholder="Select a status" multiple={false} editable={false} />
            </div>
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <TaggedSelect value={category} onChange={(newCategory) => onCategoryChange(Array.isArray(newCategory) ? newCategory[0] : newCategory)} options={availableCategories} placeholder="Select a category..." multiple={false} editable={false} />
            </div>
          </div>
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <TaggedSelect value={tags} onChange={(value) => onTagsChange(Array.isArray(value) ? value : [value])} options={availableTags} placeholder="Add tags..." multiple={true} editable={true} onCreateOption={onCreateTag} />
          </div>

          <div>
            <label htmlFor="citations" className="block text-sm font-medium text-gray-700 mb-1">Citations / Sources</label>
            <TaggedSelect
              value={citations}
              onChange={(value) => onCitationsChange(Array.isArray(value) ? value : [value])}
              options={[]}
              placeholder="Add URL (e.g. https://wikipedia.org...)"
              multiple={true}
              editable={true}
              onCreateOption={(val) => onCitationsChange([...citations, val])}
            />
            <p className="mt-1 text-sm text-gray-500">Links to authoritative sources referenced in the article.</p>
            <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2">
              <p className="text-xs text-yellow-800">
                <strong>Note:</strong> Quality over quantity. Use 3-8 meaningful citations.
                <br />
                ⚠️ Only cite sources you actually used. Irrelevant citations can harm your ranking.
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="mentions" className="block text-sm font-medium text-gray-700 mb-1">Mentions (Entities)</label>
            <TaggedSelect
              value={mentions}
              onChange={(value) => onMentionsChange(Array.isArray(value) ? value : [value])}
              options={[]}
              placeholder="Add entity name (e.g. Google, React, Agentic AI)"
              multiple={true}
              editable={true}
              onCreateOption={(val) => onMentionsChange([...mentions, val])}
            />
            <p className="mt-1 text-sm text-gray-500">People, organizations, or products mentioned in the article.</p>
            <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2">
              <p className="text-xs text-yellow-800">
                <strong>Best Practice:</strong> Tag 5-10 core entities discussed in the text.
                <br />
                ⚠️ Ensure these entities clearly appear in your content to avoid "Metadata Stuffing" penalties.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArticleMetaDialog;
